import { FluxErrorCodes } from "@buildwithflux/constants";
import { FluxBaseError } from "./FluxBaseError";
import { concatErrorArgs } from "./helpers";
/**
 * TODO: define these subtypes in relevant places, rather than centrally, here, unless they're truly used from a
 *       lot of different places
 */
/**
 * Note, this isn't FluxBaseError (the actual supertype of all our errors); this is instead a "generic" error that was
 * used in ~40 random places
 *
 * @deprecated Use UnknownError, GenericError, your own type of FluxBaseError, or Error directly
 *
 * Further rationale for deprecation, and direction for which to use:
 *   - the error code, message, and handling for this subtype, GenericError and UnknownError are completely generic, and
 *       so don't contain any information/do much for you. In many cases, you can use a plain old Error, and the "cause"
 *       option in its second constructor argument
 *   - you're better off with an error type that doesn't carry a "code" if you're not going to be reading/relying on one
 *   - if you're trying to handle the result of a catch (which is `unknown` type), where you've got something that
 *       is probably (but not definitely) an Error, then use UnknownError instead of FluxError
 *   - if you're just looking to attach an arbitrary bag of parameters to an error, create your own subtype, or use
 *       GenericError instead of FluxError
 */
export class FluxError extends FluxBaseError {
    constructor(message, cause) {
        super(FluxErrorCodes.GenericError, ...concatErrorArgs(message, cause));
    }
}
/**
 * This is better replacement for one use case of FluxError: handling unknown errors
 *
 * It directly handles an unknown "cause", which might be something string-able, or might be an Error. The data you
 * supply as the cause should probably come from a catch: it should usually be an Error or string, not completely
 * arbitrary data (which probably won't be logged correctly); for generic data, see GenericError
 *
 * QUESTION: why does this reused the error code of GenericError?
 */
export class UnknownError extends FluxBaseError {
    constructor(message, cause) {
        super(FluxErrorCodes.GenericError, ...concatErrorArgs(message, cause));
    }
}
/**
 * This is better replacement for one use case of FluxError: handling errors with generic attached data ("artifacts")
 */
export class GenericError extends FluxBaseError {
    artifacts;
    constructor(message, artifacts = {}) {
        super(FluxErrorCodes.GenericError, message);
        this.artifacts = artifacts;
    }
}
export class DocumentReducerError extends FluxBaseError {
    actionName;
    reducerFunction;
    constructor(actionName, message, reducerFunction) {
        super(FluxErrorCodes.DocumentReducerError, message);
        this.actionName = actionName;
        this.reducerFunction = reducerFunction;
    }
}
export class MissingArgumentReducerError extends FluxBaseError {
    actionName;
    reducerFunction;
    constructor(actionName, argument, reducerFunction) {
        super(FluxErrorCodes.MissingArgumentReducerError, `Missing argument '${argument}' in reducer function '${actionName}'`);
        this.actionName = actionName;
        this.reducerFunction = reducerFunction;
    }
}
export class MissingFieldReducerError extends FluxBaseError {
    actionName;
    reducerFunction;
    constructor(actionName, argument, reducerFunction) {
        super(FluxErrorCodes.MissingFieldReducerError, `Missing field '${argument}' in reducer function '${actionName}'`);
        this.actionName = actionName;
        this.reducerFunction = reducerFunction;
    }
}
export class MissingActionDisplayStringError extends FluxBaseError {
    actionName;
    constructor(actionName) {
        super(FluxErrorCodes.MissingActionDisplayStringError, `Missing description string in change history for action name '${actionName}'`);
        this.actionName = actionName;
    }
}
export class UndefinedChangeHistoryEntryError extends FluxBaseError {
    actionRecordUid;
    changeDescription;
    constructor(actionRecordUid, changeDescription) {
        super(FluxErrorCodes.UndefinedChangeHistoryEntryError, `Entry in change history for '${changeDescription}' with Action Record Uid '${actionRecordUid}' returned undefined.`);
        this.actionRecordUid = actionRecordUid;
        this.changeDescription = changeDescription;
    }
}
export class GetUserByUidError extends FluxBaseError {
    context;
    constructor(context, cause) {
        super(FluxErrorCodes.GetUserByUidError, `GetUserByUid error in '${context}'`, { cause });
        this.context = context;
    }
}
export class GetOrganizationByUidError extends FluxBaseError {
    context;
    constructor(context, cause) {
        super(FluxErrorCodes.GetUserByUidError, `GetOrganizationByUid error in '${context}'`, { cause });
        this.context = context;
    }
}
export class GetEnterpriseByUidError extends FluxBaseError {
    context;
    constructor(context, cause) {
        super(FluxErrorCodes.GetUserByUidError, `GetEnterpriseByUid error in '${context}'`, { cause });
        this.context = context;
    }
}
export class EmptyStringArgumentError extends FluxBaseError {
    functionName;
    argumentName;
    constructor(functionName, argumentName) {
        super(FluxErrorCodes.EmptyStringArgumentError, "Received empty string for argument name '" + argumentName + "' in function " + functionName);
        this.functionName = functionName;
        this.argumentName = argumentName;
    }
}
export class CaptureIntercomLeadError extends FluxBaseError {
    context;
    constructor(context, cause) {
        super(FluxErrorCodes.CaptureIntercomLeadError, ...concatErrorArgs(`CaptureIntercomLeadError error in '${context}'`, cause));
        this.context = context;
    }
}
export class KiCadPartImporterError extends FluxBaseError {
    constructor(message, options = {}) {
        super(FluxErrorCodes.KiCadPartImporterError, message, options);
    }
}
export class GerberExporterError extends FluxBaseError {
    constructor(message, options = {}) {
        super(FluxErrorCodes.GerberExporterError, message, options);
    }
}
export class ExportError extends FluxBaseError {
    constructor(message, options = {}) {
        super(FluxErrorCodes.ExportError, message, options);
    }
}
export class WriteActionRecordError extends FluxBaseError {
    actionRecord;
    constructor(message, actionRecord, cause) {
        super(FluxErrorCodes.WriteActionRecordError, ...concatErrorArgs(message, cause));
        this.actionRecord = actionRecord;
    }
}
export class InvalidFirebaseEnvironmentError extends FluxBaseError {
    constructor(message, options = {}) {
        super(FluxErrorCodes.InvalidFirebaseEnvironmentError, message, options);
    }
}
export class UserSignInError extends FluxBaseError {
    constructor(message, cause) {
        super(FluxErrorCodes.UserSignInError, ...concatErrorArgs(message, cause));
    }
}
export class AnonymousSignInError extends FluxBaseError {
    constructor(message, cause) {
        super(FluxErrorCodes.AnonymousSignInError, ...concatErrorArgs(message, cause));
    }
}
export class UserSignUpError extends FluxBaseError {
    userUid;
    constructor(message, userUid, cause) {
        super(FluxErrorCodes.UserSignUpError, message, { cause });
        this.userUid = userUid || "undefined";
    }
}
export class CreateUserError extends FluxBaseError {
    constructor(message, cause) {
        super(FluxErrorCodes.CreateUserError, message, { cause });
    }
}
export class InvalidActionRecordError extends FluxBaseError {
    constructor(message, options = {}) {
        super(FluxErrorCodes.InvalidActionRecordError, message, options);
    }
}
export class FetchActionRecordsError extends FluxBaseError {
    constructor(message, cause) {
        super(FluxErrorCodes.FetchActionRecordsError, message, { cause });
    }
}
export class GetPartError extends FluxBaseError {
    partName;
    constructor(partUid, cause) {
        super(FluxErrorCodes.GetPartError, `Failed to get part with uid: ${partUid}`, { cause });
        this.partName = partUid;
    }
}
export class InvalidRequestError extends FluxBaseError {
    constructor(message, cause) {
        super(FluxErrorCodes.InvalidRequestError, message, { cause });
    }
}
/**
 * NOTE: these are filtered out from preview builds, and UI tests to
 * avoid noise. See LogConnector captureError.
 */
export class PerformanceBudgetViolationError extends FluxBaseError {
    constructor(durationInMs, budgetInMs, unitPostFix = "ms", key = "unknown") {
        super(FluxErrorCodes.PerformanceBudgetViolationError, `Performance Budget Violation Error (${key})! Exceeded ${budgetInMs}${unitPostFix} budget by ${durationInMs - budgetInMs}${unitPostFix}. Total ${durationInMs}${unitPostFix}`);
    }
}
export class PerformanceMeasurementError extends FluxBaseError {
    constructor(traceName) {
        super(FluxErrorCodes.PerformanceMeasurementError, `startTime is undefined when measuring trace ${traceName}`);
    }
}
export class PublishNewPartError extends FluxBaseError {
    partDocumentUid;
    constructor(partDocumentUid, cause) {
        super(FluxErrorCodes.PublishNewPartError, `Publishing new part did not succeed`, { cause });
        this.partDocumentUid = partDocumentUid;
    }
}
export class PublishNewPartVersionError extends FluxBaseError {
    partDocumentUid;
    constructor(partDocumentUid, cause) {
        super(FluxErrorCodes.PublishNewPartVersionError, `Publishing new part version did not succeed`, { cause });
        this.partDocumentUid = partDocumentUid;
    }
}
export class ApplyLatestDraftError extends FluxBaseError {
    partDocumentUid;
    constructor(partDocumentUid, cause) {
        super(FluxErrorCodes.ApplyLatestDraftError, `Applying HEAD version to document failed`, { cause });
        this.partDocumentUid = partDocumentUid;
    }
}
export class ApplyPartVersionError extends FluxBaseError {
    documentUid;
    partUidForVersion;
    targetVersion;
    constructor(documentUid, partUidForVersion, targetVersion) {
        super(FluxErrorCodes.ApplyPartVersionError, "Error while applying part versions");
        this.documentUid = documentUid;
        this.partUidForVersion = partUidForVersion;
        this.targetVersion = targetVersion;
    }
}
export class ReceiveLatestDraftsError extends FluxBaseError {
    partDocumentUid;
    constructor(partDocumentUid, cause, variant) {
        super(variant === "start" ? FluxErrorCodes.StartReceivingDraftsError : FluxErrorCodes.StopReceivingDraftsError, `${variant} receive latest drafts failed`, { cause });
        this.partDocumentUid = partDocumentUid;
    }
}
export class ThreeDModelConversionError extends FluxBaseError {
    constructor(message, options = {}) {
        super(FluxErrorCodes.ThreeDModelConversionError, message, options);
    }
}
export class FeatureFlagNotExistsError extends FluxBaseError {
    featureFlag;
    constructor(featureFlag, whereNotFound) {
        super(whereNotFound === "flux"
            ? FluxErrorCodes.FeatureFlagNotConfiguredError
            : FluxErrorCodes.FeatureFlagNotRetrievedError, `Feature flag ${featureFlag} was not found in ${whereNotFound}.`);
        this.featureFlag = featureFlag;
    }
}
export class FeatureFlagClientNotInitializedError extends FluxBaseError {
    constructor(cause) {
        super(FluxErrorCodes.FeatureFlagClientNotInitializedError, `Feature flag client failed to initialize`, { cause });
    }
}
export class DeleteProjectError extends FluxBaseError {
    constructor(cause) {
        super(FluxErrorCodes.DeleteProjectError, `Error when deleting a project`, { cause });
    }
}
export class AlgoliaEventError extends FluxBaseError {
    constructor(context, cause) {
        super(FluxErrorCodes.DeleteProjectError, ...concatErrorArgs(`Error sending Algolia Event in ${context}`, cause));
    }
}
export class DocumentExceededMaximumFirestoreDepthError extends FluxBaseError {
    constructor() {
        super(FluxErrorCodes.DocumentExceededMaximumFirestoreDepthError, `Document depth exceeded maximum allowed depth in firebase.`);
    }
}
export class WriteDocumentConfigError extends FluxBaseError {
    constructor(message, options = {}) {
        super(FluxErrorCodes.WriteDocumentConfigError, message, options);
    }
}
export class InvalidDxfShapeAssetError extends FluxBaseError {
    constructor(message, cause) {
        super(FluxErrorCodes.InvalidLayoutShapeDxfError, ...concatErrorArgs(message, cause));
    }
}
export class InvalidSvgShapeAssetError extends FluxBaseError {
    constructor(message, cause) {
        super(FluxErrorCodes.InvalidLayoutShapeSvgError, ...concatErrorArgs(message ?? "The SVG file must contain one closed shape.", cause));
    }
}
export class MissingChildUidError extends FluxBaseError {
    constructor(context, cause) {
        super(FluxErrorCodes.MissingChildUidError, ...concatErrorArgs(`childUid missing in ${context}`, cause));
    }
}
export class WebGLError extends FluxBaseError {
    constructor(message) {
        super(FluxErrorCodes.WebGLError, message);
    }
}
//# sourceMappingURL=FluxErrors.js.map